"use client";

import React from "react";
import dynamic from "next/dynamic";
import SimpleButton from "@/components/SimpleButton";
import ConditionalClientComponent from "@/components/ConditionalClientComponent";
import LoadingSpinner from "@/components/LoadingSpinner";

// Dynamic imports for client components (manual code-splitting)
const DynamicHeavyChart = dynamic(() => import("@/components/HeavyChart"), {
  loading: () => <LoadingSpinner message="Loading Interactive Chart..." />,
  ssr: false,
});

const DynamicHeavyCalculator = dynamic(
  () => import("@/components/HeavyCalculator"),
  {
    loading: () => (
      <LoadingSpinner message="Loading Interactive Calculator..." />
    ),
    ssr: false,
  }
);

export default function ClientDynamicSection() {
  return (
    <>
      {/* Dynamic Imports Section */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-lg border p-6 mb-6">
          <h2 className="text-2xl font-bold text-blue-600 mb-4">
            🔵 Dynamic Imports (Manual Code-Splitting)
          </h2>
          <p className="text-gray-700 mb-4">
            These client components are loaded on-demand using next/dynamic.
            Perfect for interactive features that aren&apos;t needed
            immediately.
          </p>

          <div className="bg-black p-4 rounded-lg mb-4">
            <pre className="text-sm overflow-x-auto">
              <code>{`// ✅ Dynamic Import (manual code-splitting)
const DynamicComponent = dynamic(
  () => import('./HeavyComponent'),
  {
    loading: () => <LoadingSpinner />,
    ssr: false // Optional: disable SSR
  }
);`}</code>
            </pre>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              onClick={() => {
                const chartContainer = document.getElementById("dynamic-chart");
                if (chartContainer) {
                  chartContainer.style.display =
                    chartContainer.style.display === "none" ? "block" : "none";
                }
              }}
            >
              📊 Toggle Interactive Chart (Dynamic)
            </button>
            <div id="dynamic-chart" style={{ display: "none" }}>
              <DynamicHeavyChart title="Dynamically Loaded Chart" />
            </div>
          </div>

          <div className="space-y-4">
            <button
              className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              onClick={() => {
                const calcContainer = document.getElementById("dynamic-calc");
                if (calcContainer) {
                  calcContainer.style.display =
                    calcContainer.style.display === "none" ? "block" : "none";
                }
              }}
            >
              🧮 Toggle Calculator (Dynamic)
            </button>
            <div id="dynamic-calc" style={{ display: "none" }}>
              <DynamicHeavyCalculator title="Dynamically Loaded Calculator" />
            </div>
          </div>
        </div>
      </div>

      {/* Regular Client Components Section */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-lg border p-6 mb-6">
          <h2 className="text-2xl font-bold text-orange-600 mb-4">
            🟡 Regular Client Components (Main Bundle)
          </h2>
          <p className="text-gray-700 mb-4">
            Small, lightweight components that should remain in the main bundle.
            These are typically core UI elements used throughout your
            application.
          </p>

          <div className="bg-black p-4 rounded-lg mb-4">
            <pre className="text-sm overflow-x-auto">
              <code>{`// ❌ Don't dynamically import lightweight components
'use client';
import React from 'react';

export default function SimpleButton() {
  // Lightweight component with minimal dependencies
  return <button>Click me</button>;
}`}</code>
            </pre>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <SimpleButton label="Regular Button (Main Bundle)" />
          <div className="bg-white p-4 rounded-lg shadow border">
            <h3 className="text-lg font-semibold mb-3 text-gray-800">
              Main Bundle Components
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              These components should stay in the main bundle for optimal
              performance
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span>Navigation Menu</span>
                <span className="text-green-600 text-xs">✓ Main Bundle</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span>Header/Footer</span>
                <span className="text-green-600 text-xs">✓ Main Bundle</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span>Basic Form Inputs</span>
                <span className="text-green-600 text-xs">✓ Main Bundle</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span>Loading Spinners</span>
                <span className="text-green-600 text-xs">✓ Main Bundle</span>
              </div>
            </div>
          </div>
        </div>

        {/* Conditional Dynamic Loading Example */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-blue-600 mb-4">
            🔄 Conditional Dynamic Loading
          </h3>
          <ConditionalClientComponent />
        </div>
      </div>
    </>
  );
}
